// src/hooks/useInboxMode.js
//
// Pass 85.1B.1 (§21/§22) — the ONE communication-mode contract shared by GlobalHeader (the badge) and InboxPanel (the
// inbox), so they can never disagree. The ACTIVE WORKSPACE — not the mere possession of a staff capability — decides
// whether a signed-in human reads communications as a STUDENT or as STAFF:
//
//   • personal ("My Coaching")  → 'student'  — reads via the canonical student ACCESS LANE (participant + selected org).
//                                              A dual-role human (participant + instructor/owner) in Personal is a
//                                              STUDENT here; a staff capability never overrides Personal.
//   • instructor / organization → 'staff'    — reads org-scoped staff notifications/threads (rules require current org
//                                              staff). The authorised org is the user's own org.
//   • admin                     → 'staff'     — platform admin uses the same org-scoped staff read (empty when no org).
//   • student_preview           → 'none'      — an operator previewing a student NEVER sees the previewed student's
//                                              private inbox, and we do not surface the operator's own badge in preview.
//
// In 'staff'/'none' modes we resolve NO participant profiles (useStudentContext is called with '' → zero reads), so a
// staff surface performs no student comms I/O; in 'student' mode we resolve the lane and only a MODERN lane yields a
// scoped org+participant (every unresolved lane fails closed to no reads).

import { useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useWorkspace } from '../WorkspaceContext';
import { useStudentContext } from './useStudentContext';
import { canEnterInstructorWorkspace } from '../utils/platformAccess';

export function useInboxMode() {
  const { currentUser, userData } = useAuth() || {};
  const { currentWorkspace } = useWorkspace() || {};
  const uid = currentUser?.uid || '';

  const mode = currentWorkspace === 'personal' ? 'student'
    : (currentWorkspace === 'instructor' || currentWorkspace === 'organization' || currentWorkspace === 'admin') ? 'staff'
    : 'none'; // student_preview (and any unknown) → no comms

  // Resolve the student context ONLY in student mode (uid '' → useStudentContext performs zero participant reads).
  const ctx = useStudentContext(mode === 'student' ? uid : '');
  // Pass 85.1B.2 (§20) — SINGLE-ORG staff scope. The authorised org is the account's flat org; Motus does not yet expose a
  // multi-org staff SELECTOR, so a staff user's inbox is scoped to this one org. The org-scoped queries (userId+org /
  // org+instructorId) make cross-org reads impossible regardless, so this is a UI-completeness limitation, not a leak.
  const staffOrg = String(userData?.orgId || userData?.organizationId || '');
  // Pass 85.1B.2 (Blocker A / §4/§6) — coaching THREADS are readable only by the actual instructor/reviewer (rules:
  // threadCurrentOrgStaff = owner/admin/instructor + participant). A PURE OPERATOR (ops, or a non-teaching owner/admin) has
  // NO coaching-thread authority, and a pure-ops list query is structurally denied by the rules — so the inbox must NOT
  // issue it and instead show an honest "no conversations" state. Only an account with a teaching relationship reads threads.
  const staffReadsThreads = mode === 'staff' && !!staffOrg && canEnterInstructorWorkspace(userData || {}, staffOrg);

  return useMemo(() => {
    const lane = mode === 'student' ? ctx.lane : 'n/a';
    const isModern = mode === 'student' && lane === 'modern';
    return {
      uid,
      mode,
      lane,
      ctx,
      // Student scope — populated ONLY for a resolved MODERN lane (else '' → hooks fail closed, no reads).
      studentOrg: isModern ? String(ctx.selectedOrganizationId || '') : '',
      studentPid: isModern ? String(ctx.activeParticipantId || '') : '',
      // Staff scope — the authorised org (empty in admin with no org → staff read stays closed).
      staffOrg: mode === 'staff' ? staffOrg : '',
      // Staff coaching-thread capability — TRUE only for a teaching account (ops/pure-operator → false → no thread query).
      staffReadsThreads,
    };
  }, [uid, mode, ctx, staffOrg, staffReadsThreads]);
}

export default useInboxMode;
